import {Button, Dialog, Flex, keyCodes} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import React, {SyntheticEvent, useRef, useState, useEffect, useCallback} from 'react';
import {helpPopoverRef} from '../../../../_shared/Help/constants';
import {generateReportColumns} from '../../services/service';
import {reportGeneratorsStore} from '../../stores/ReportGeneratorsStore';
import texts from '../../texts';
import {CloseWithHelp} from '../CloseWithHelp';
import Content from './Content';
import styles from './styles.module.scss';
import {useFieldSelect} from './useFieldSelect';
import {
    showConfirmMessage,
    showWarnMessage
} from '../../../../_shared/Messages/actions/MessageActions';
import {sendAnalyticsMonitor} from '../../../../utils/monitoring';
import {monitorTransactionValues} from '../../consts';
import {priority} from '@priority-software/priority-proto-api/lib';
import {useMutation} from 'react-query';
import {ScreenLocker} from '../../../../features/Form/components/ScreenLocker';
import {createDataTestId} from '../../../../utils/dataTestId';
import {handleFieldsUpdate} from '../../utils';
import {resetOuterJoinId} from '../../actions';

interface Props {
    fields: priority.netitems.nrest.ITreeNode[];
    onClose: () => void;
    isLoadingFields: boolean;
}

const FieldSelect = observer(({fields, isLoadingFields, onClose}: Props) => {
    const {currentScreen} = reportGeneratorsStore;
    const {currentReport, session, forms} = currentScreen;
    const {
        updateColumns,
        setSelectedNodes,
        nodeId,
        searchQuery,
        setSearchQuery,
        reportId,
        duplicateId,
        setLinkId,
        linkId,
        selectedNodes: initSelectedNodes,
        setSavedOuterJoinId,
        savedOuterJoinId,
        getOuterJoinId
    } = currentReport;

    const fieldsActions = useFieldSelect(initSelectedNodes, fields, forms.get(nodeId), session);

    const [isCanceled, setCanceled] = useState(false);
    const refBtn = useRef<HTMLButtonElement>(null);
    const {selectedNodes, isDirty} = fieldsActions;

    const isOuterJoinIdFlag = useCallback(() => {
        const currOuterjoinId = currentReport.getOuterJoinId();
        if (!currOuterjoinId && !savedOuterJoinId) {
            return false;
        } else if (
            !isNaN(Number(currOuterjoinId)) &&
            Number(currOuterjoinId) !== savedOuterJoinId
        ) {
            return true;
        } else {
            return false;
        }
    }, [currentReport.getOuterJoinId(), savedOuterJoinId]);

    const handleUpdate = async () => {
        const selectedNodesIds = Array.from(selectedNodes.keys());
        return await generateReportColumns(selectedNodesIds, duplicateId || reportId, linkId);
    };

    const {mutate: updateFields, isLoading} = useMutation(handleUpdate, {
        onSuccess: data => {
            const resColumns = data?.columns
                ? data?.columns.map(col => ({
                      ...col,
                      SortType: priority.netitems.nrest.RepColSortType.RepColSortType_Ascending
                  }))
                : [];
            handleFieldsUpdate(resColumns);
            updateColumns(resColumns || []);
            setLinkId(data?.linkId);
            setSelectedNodes(selectedNodes);
            sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSelectSave);
            handleClose();
        },
        onError: errorMessage => {
            showWarnMessage({
                content: (errorMessage as Error)?.message
            });
        }
    });

    if (isCanceled && refBtn.current) {
        requestAnimationFrame(() => {
            refBtn.current?.focus();
        });
        setCanceled(false);
    }

    const onCancel = () => {
        if (isDirty || isOuterJoinIdFlag()) {
            showConfirmMessage({
                title: texts.exitNoSave,
                confirmButtonText: texts.yes,
                cancleButtonText: texts.no,
                defaultButton: 0,
                onConfirm: () => {
                    sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSelectChangesYes);
                    isOuterJoinIdFlag() && resetOuterJoinId();
                    handleClose();
                },
                onCancel: () => {
                    sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSelectChangesNo);
                    setCanceled(true);
                }
            });
        } else {
            handleClose();
        }
    };

    const onSave = () => {
        if (isOuterJoinIdFlag()) {
            setSavedOuterJoinId(currentReport.getOuterJoinId());
            if (!isDirty) {
                handleClose();
            }
        }
        if (isDirty) {
            updateFields();
        }
    };

    const handleKeyDown = e => {
        if (e.keyCode === keyCodes.ESC) {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isDirty]);

    const handleClose = () => {
        setSearchQuery('');
        onClose();
    };

    const handleHelp = e => {
        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSelectHelp);
        helpPopoverRef.current.showHelpPopover({content: texts.selectFieldHelp, anchor: e.target});
    };

    return (
        <>
            <Dialog
                isOpen={true}
                closable={true}
                width={980}
                componentClasses={styles.dialog}
                darkMask
                keyboard={false}
                onClose={(e: SyntheticEvent<HTMLDivElement, Event>) => {
                    sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSelectX);
                    onCancel();
                }}
                closeIcon={
                    <CloseWithHelp
                        onHelp={handleHelp}
                        withText
                        componentClasses={styles.headerIcons}
                    />
                }
                content={
                    <Content
                        searchQuery={searchQuery}
                        changeQuery={setSearchQuery}
                        isLoadingFields={isLoadingFields}
                        outerJoinId={getOuterJoinId()}
                        {...fieldsActions}
                    />
                }
                footer={
                    <Footer
                        refBtn={refBtn}
                        disableSave={!(isDirty || isOuterJoinIdFlag()) || !selectedNodes?.size}
                        onCancel={() => {
                            sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSelectCancel);
                            onCancel();
                        }}
                        onUpdate={onSave}
                    />
                }
            />
            <ScreenLocker showLoader={isLoading} locked={isLoading} />
        </>
    );
});

const Footer = ({onUpdate, onCancel, disableSave, refBtn}) => {
    return (
        <Flex
            componentClasses={styles.footer}
            justifyContent="flex-end"
            {...createDataTestId('new-report-select-fields-dlg-footer', true)}
        >
            <Button inputRef={refBtn} size="medium" wideButton skin="secondary" onClick={onCancel}>
                {texts.cancel}
            </Button>
            <Button disabled={disableSave} size="medium" wideButton onClick={onUpdate}>
                {texts.save}
            </Button>
        </Flex>
    );
};

export default FieldSelect;
